import React, { useState, useMemo, useEffect } from 'react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  ReferenceLine,
  Dot
} from 'recharts';
import { 
  Card, 
  H3, 
  Button, 
  ButtonGroup, 
  Tag, 
  Intent,
  HTMLSelect,
  InputGroup,
  Popover,
  Menu,
  MenuItem
} from '@blueprintjs/core';

const COLORS = [
  '#2E7CD6', // Blue
  '#00C49F', // Teal
  '#FFBB28', // Orange
  '#FF8042', // Red-Orange
  '#8884D8', // Purple
  '#82CA9D', // Green
  '#FFC658', // Yellow
  '#8DD1E1', // Light Blue
];

function EloHistoryChart({ playerStats, history, eloHistories, firebaseEloHistories, playerStatuses = {} }) {
  console.log('📊 EloHistoryChart - eloHistories:', eloHistories);
  console.log('📊 EloHistoryChart - firebaseEloHistories:', firebaseEloHistories);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [timeRange, setTimeRange] = useState('all'); // Default to showing all data from first ACT
  // Removed highlightWins feature
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const normalizePlayerName = (name = '') => name.toLowerCase().trim().replace(/\s+/g, ' ');

  const isPresetExcludedPlayer = (name = '') => normalizePlayerName(name) === 'tucker';

  const normalizedPlayerStatuses = useMemo(() => {
    const normalized = {};
    Object.entries(playerStatuses || {}).forEach(([name, status]) => {
      normalized[normalizePlayerName(name)] = status;
    });
    return normalized;
  }, [playerStatuses]);

  const isCurrentPlayer = (name = '') => {
    const exactStatus = playerStatuses?.[name];
    const normalizedStatus = normalizedPlayerStatuses[normalizePlayerName(name)];
    const status = exactStatus || normalizedStatus || 'active';
    return status !== 'retired';
  };

  const getDisplayNameScore = (name = '') => {
    const trimmed = name.trim();
    let score = 0;
    if (trimmed === name) score += 2;
    if (/[A-Z]/.test(name)) score += 2;
    if (!name.includes('_')) score += 1;
    return score;
  };

  const getPlayerDataScore = (player) => {
    const calculatedCount = player.eloHistory?.length || 0;
    const firebaseCount = firebaseEloHistories?.[player.name]?.length || 0;
    const eloScore = Number.isFinite(player.elo) ? player.elo : 0;
    return (firebaseCount * 10000) + (calculatedCount * 100) + eloScore;
  };

  // Get all players with ELO ratings for selection
  const allPlayersWithElo = useMemo(() => {
    const dedupedByName = new Map();

    playerStats
      .filter(p => {
        const hasCalculatedHistory = p.eloHistory && p.eloHistory.length > 0;
        const hasFirebaseHistory = firebaseEloHistories && firebaseEloHistories[p.name] && firebaseEloHistories[p.name].length > 0;
        return p.hasEloRating && (hasCalculatedHistory || hasFirebaseHistory);
      })
      .forEach((player) => {
        const normalized = normalizePlayerName(player.name);
        const existing = dedupedByName.get(normalized);

        if (!existing) {
          dedupedByName.set(normalized, player);
          return;
        }

        const existingScore = getPlayerDataScore(existing);
        const nextScore = getPlayerDataScore(player);
        const existingNameScore = getDisplayNameScore(existing.name);
        const nextNameScore = getDisplayNameScore(player.name);

        if (
          nextScore > existingScore ||
          (nextScore === existingScore && nextNameScore > existingNameScore) ||
          (nextScore === existingScore && nextNameScore === existingNameScore && player.name.length < existing.name.length)
        ) {
          dedupedByName.set(normalized, player);
        }
      });

    return Array.from(dedupedByName.values()).sort((a, b) => {
      const aCurrent = isCurrentPlayer(a.name) ? 1 : 0;
      const bCurrent = isCurrentPlayer(b.name) ? 1 : 0;
      if (bCurrent !== aCurrent) return bCurrent - aCurrent;
      if ((b.elo || 0) !== (a.elo || 0)) return (b.elo || 0) - (a.elo || 0);
      return a.name.localeCompare(b.name);
    });
  }, [playerStats, firebaseEloHistories, playerStatuses, normalizedPlayerStatuses]);
  
  // Get top players by ELO for quick selection
  const topPlayers = useMemo(() => {
    return allPlayersWithElo
      .filter(player => !isPresetExcludedPlayer(player.name))
      .slice(0, 10);
  }, [allPlayersWithElo]);

  // Normalize old selections if player names are deduped/cleaned
  useEffect(() => {
    if (allPlayersWithElo.length === 0) return;

    const canonicalByNormalizedName = new Map(
      allPlayersWithElo.map(player => [normalizePlayerName(player.name), player.name])
    );

    setSelectedPlayers(prev => {
      const next = [];
      const seen = new Set();

      prev.forEach((name) => {
        const canonical = canonicalByNormalizedName.get(normalizePlayerName(name));
        if (!canonical) return;

        const normalized = normalizePlayerName(canonical);
        if (seen.has(normalized)) return;
        seen.add(normalized);
        next.push(canonical);
      });

      const unchanged =
        next.length === prev.length &&
        next.every((name, index) => name === prev[index]);

      return unchanged ? prev : next;
    });
  }, [allPlayersWithElo]);
  
  // Auto-select top 3 players on first load
  useEffect(() => {
    if (selectedPlayers.length === 0 && topPlayers.length >= 3) {
      setSelectedPlayers(topPlayers.slice(0, 3).map(p => p.name));
    }
  }, [topPlayers]);

  // Process ELO history data for chart
  const chartData = useMemo(() => {
    console.log('📊 Selected players:', selectedPlayers);
    console.log('📊 Available eloHistories:', Object.keys(eloHistories || {}));
    console.log('📊 Available firebaseEloHistories:', Object.keys(firebaseEloHistories || {}));
    
    if (selectedPlayers.length === 0) {
      console.log('📊 No players selected, returning empty data');
      return [];
    }

    // Prioritize calculated history (from eloHistories) which has all tournament data
    // Only use Firebase history as a fallback or for supplementary data
    const hasCalculatedHistories = eloHistories && Object.keys(eloHistories).length > 0;
    
    // Collect player histories  
    const playerHistoriesMap = {};
    const playerDataMap = {}; // Store the actual data points for each player

    selectedPlayers.forEach(playerName => {
      const combinedHistory = [];
      const player = playerStats.find(p => p.name === playerName);
      
      // Check if player is in Firebase ELO_history database
      const hasFirebaseHistory = firebaseEloHistories && firebaseEloHistories[playerName] && firebaseEloHistories[playerName].length > 0;
      
      if (hasFirebaseHistory) {
        console.log(`📊 ${playerName} is in Firebase ELO_history - using first ELO_history entry as starting point + calculated progression`);
        
        // For players in Firebase ELO_history: Use FIRST ELO_history entry as baseline + show ALL ACTs
        const playerFirebaseHistory = firebaseEloHistories[playerName];
        
        // Get the very first ELO_history entry as the starting baseline
        const sortedFirebaseHistory = [...playerFirebaseHistory].sort((a, b) => a.at - b.at);
        const firstFirebaseEntry = sortedFirebaseHistory[0];
        const startingElo = firstFirebaseEntry ? firstFirebaseEntry.value : (player ? player.elo : 500);
        
        console.log(`📊 ${playerName}'s starting ELO (from first ELO_history entry): ${startingElo}`);
        
        // Find their first ACT to establish timeline start
        let firstActTimestamp = null;
        if (history && history.length > 0) {
          const playerTournaments = history.filter(act => {
            return Object.values(act.teams || {}).some(team => 
              Object.values(team.players || {}).includes(playerName)
            );
          }).sort((a, b) => new Date(a.date) - new Date(b.date));
          
          if (playerTournaments.length > 0) {
            firstActTimestamp = new Date(playerTournaments[0].date).getTime();
            console.log(`📊 ${playerName}'s first ACT was on:`, playerTournaments[0].date);
            
            const baselineTimestamp = firstActTimestamp || firstFirebaseEntry?.at || null;
            if (baselineTimestamp) {
              combinedHistory.push({
                timestamp: baselineTimestamp,
                elo: startingElo,
                date: new Date(baselineTimestamp),
                change: 0,
                event: `Starting ELO (${startingElo} from first ELO_history)`,
                finish: null,
                source: 'initial'
              });
            }
          }
        }
        
        // For players in Firebase ELO_history: ONLY use Firebase entries, stay flat at first value until next entry
        console.log(`📊 Adding ONLY Firebase history for ${playerName}:`, playerFirebaseHistory.length, 'entries (staying flat at ${startingElo} until next entry)');
        
        // Add all Firebase history entries (these are the only real ELO changes)
        const firebaseBaselineTimestamp = firstActTimestamp || firstFirebaseEntry?.at || 0;
        playerFirebaseHistory
          .filter(entry => entry.at >= firebaseBaselineTimestamp)
          .forEach(entry => {
          combinedHistory.push({
            timestamp: entry.at,
            elo: entry.value,
            date: new Date(entry.at),
            source: 'firebase'
          });
        });
        
      } else {
        console.log(`📊 ${playerName} NOT in Firebase ELO_history - using calculated history as backup`);
        
        // For players NOT in Firebase ELO_history: Use calculated history as backup
        
        // Find earliest tournament participation from history for timeline start
        let firstActTimestamp = null;
        if (history && history.length > 0) {
          const playerTournaments = history.filter(act => {
            return Object.values(act.teams || {}).some(team => 
              Object.values(team.players || {}).includes(playerName)
            );
          }).sort((a, b) => new Date(a.date) - new Date(b.date));
          
          if (playerTournaments.length > 0) {
            firstActTimestamp = new Date(playerTournaments[0].date).getTime();
            console.log(`📊 ${playerName}'s first ACT was on:`, playerTournaments[0].date);
          }
        }
        
        const playerCurrentElo = player ? player.elo : 500;
        const calculatedHistory = hasCalculatedHistories && eloHistories[playerName] ? eloHistories[playerName] : [];
        const calculatedFirstTimestamp = calculatedHistory.length > 0
          ? Math.min(...calculatedHistory.map(entry => new Date(entry.date).getTime()))
          : null;
        const playerStatsFirstTimestamp = player && player.eloHistory && player.eloHistory.length > 0
          ? Math.min(...player.eloHistory.map(entry => new Date(entry.date).getTime()))
          : null;
        const baselineTimestamp = firstActTimestamp || calculatedFirstTimestamp || playerStatsFirstTimestamp || null;
        
        if (baselineTimestamp) {
          combinedHistory.push({
            timestamp: baselineTimestamp,
            elo: playerCurrentElo,
            date: new Date(baselineTimestamp),
            change: 0,
            event: `Starting ELO (${playerCurrentElo} baseline)`,
            finish: null,
            source: 'initial'
          });
        }
        
        // Get calculated history from all ACT tournaments (filter to player's first ACT/data point)
        if (calculatedHistory.length > 0) {
          console.log(`📊 Found calculated history for ${playerName}:`, calculatedHistory.length, 'entries');
          
          const filteredHistory = calculatedHistory.filter(entry => {
            const timestamp = new Date(entry.date).getTime();
            return baselineTimestamp ? timestamp >= baselineTimestamp : true;
          });
          combinedHistory.push(...filteredHistory.map(entry => ({
            timestamp: new Date(entry.date).getTime(),
            elo: entry.elo,
            date: new Date(entry.date),
            change: entry.change,
            event: entry.event,
            finish: entry.finish,
            source: 'calculated'
          })));
        }
        
        // Also get playerStats eloHistory if different
        if (player && player.eloHistory) {
          console.log(`📊 Found playerStats history for ${playerName}:`, player.eloHistory.length, 'entries');
          player.eloHistory.forEach(entry => {
            const timestamp = new Date(entry.date).getTime();
            if (baselineTimestamp && timestamp < baselineTimestamp) return;
            // Only add if we don't already have this timestamp
            if (!combinedHistory.some(h => h.timestamp === timestamp)) {
              combinedHistory.push({
                timestamp: timestamp,
                elo: entry.elo,
                date: new Date(entry.date),
                change: entry.change,
                event: entry.event,
                finish: entry.finish,
                source: 'playerStats'
              });
            }
          });
        }
      }
      
      if (combinedHistory.length > 0) {
        // Sort combined history by timestamp
        combinedHistory.sort((a, b) => a.timestamp - b.timestamp);
        playerDataMap[playerName] = combinedHistory;
        console.log(`📊 Combined history for ${playerName}:`, combinedHistory.length, 'total entries');
        
        // Store player history for processing
      }
      
      // Sort player's data by timestamp
      if (playerDataMap[playerName]) {
        playerDataMap[playerName].sort((a, b) => a.timestamp - b.timestamp);
        playerHistoriesMap[playerName] = playerDataMap[playerName];
      }
    });

    const isActualEloChangeEntry = (entry) => {
      if (!entry) return false;
      if (entry.source === 'initial') return false;
      if (typeof entry.event === 'string' && entry.event.startsWith('Initial')) return false;
      if (typeof entry.change === 'number') return entry.change !== 0;
      return true;
    };

    // Collect all unique timestamps from player histories
    const allPlayerTimestamps = new Set();
    let firstChangeTimestamp = null;
    Object.values(playerDataMap).forEach(playerHistory => {
      playerHistory.forEach(entry => {
        allPlayerTimestamps.add(entry.timestamp);
        if (isActualEloChangeEntry(entry)) {
          if (firstChangeTimestamp === null || entry.timestamp < firstChangeTimestamp) {
            firstChangeTimestamp = entry.timestamp;
          }
        }
      });
    });
    const sortedTimestamps = Array.from(allPlayerTimestamps).sort((a, b) => a - b);

    // Apply time range filter
    let filteredTimestamps = sortedTimestamps;
    let rangeCutoffTimestamp = null;
    if (timeRange !== 'all') {
      const daysAgo = parseInt(timeRange.replace('last', ''));
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysAgo);
      rangeCutoffTimestamp = cutoffDate.getTime();
      filteredTimestamps = sortedTimestamps.filter(timestamp => timestamp >= cutoffDate.getTime());
    }

    // Build chart data with monthly intervals for symmetrical display
    const data = [];
    const playerLatestElo = {};

    // Start from the first actual ELO change (or the selected time-range cutoff)
    const firstVisibleTimestamp = filteredTimestamps.length > 0 ? filteredTimestamps[0] : sortedTimestamps[0];
    const timelineStartTimestamp = rangeCutoffTimestamp ?? firstChangeTimestamp ?? firstVisibleTimestamp ?? Date.now();
    const startDate = new Date(timelineStartTimestamp);
    const endDate = new Date(); // TODAY - will automatically update
    
    // Generate monthly intervals from the first visible point to today for proportional spacing
    const monthlyTimestamps = [];
    const current = new Date(startDate);
    
    // Generate monthly markers for timeline structure
    while (current <= endDate) {
      monthlyTimestamps.push(current.getTime());
      current.setMonth(current.getMonth() + 1);
    }
    
    console.log('📊 Monthly intervals from first visible point to today:', monthlyTimestamps.length, 'months');
    console.log('📊 Filtered timestamps available:', filteredTimestamps.length);
    
    // Combine monthly markers with actual data points
    const visibleDataTimestamps = filteredTimestamps.filter(timestamp => timestamp >= startDate.getTime());
    console.log('📊 Data points in visible range:', visibleDataTimestamps.length);
    
    const visibleMonthlyTimestamps = rangeCutoffTimestamp === null
      ? monthlyTimestamps
      : monthlyTimestamps.filter(timestamp => timestamp >= rangeCutoffTimestamp);
    const allTimestamps = [...new Set([...visibleMonthlyTimestamps, ...visibleDataTimestamps])].sort((a, b) => a - b);
    console.log('📊 All timestamps for chart:', allTimestamps.length);
      
      allTimestamps.forEach((timestamp, index) => {
        const date = new Date(timestamp);
        const isMonthlyMarker = monthlyTimestamps.includes(timestamp);
        
        const dataPoint = {
          date: date.toLocaleDateString('en-US', { 
            month: 'short', 
            year: '2-digit'
          }),
          fullDate: date.toISOString(),
          timestamp: timestamp,
          index: index,
          isMonthlyMarker: isMonthlyMarker,
          uniqueKey: `${timestamp}-${index}` // Add unique key to prevent duplicates
        };

      // Add each player's ELO at this timestamp
      selectedPlayers.forEach(playerName => {
        const playerHistory = playerHistoriesMap[playerName];
        if (playerHistory && playerHistory.length > 0) {
          // Find exact match for this timestamp
          const exactEntry = playerHistory.find(e => e.timestamp === timestamp);
          
          if (exactEntry) {
            // We have an exact data point for this timestamp
            dataPoint[playerName] = exactEntry.elo;
            playerLatestElo[playerName] = exactEntry.elo;
            
            // Add metadata for tooltips
            dataPoint[`${playerName}_change`] = exactEntry.change || 0;
            dataPoint[`${playerName}_event`] = exactEntry.event || '';
            dataPoint[`${playerName}_finish`] = exactEntry.finish;
            dataPoint[`${playerName}_source`] = exactEntry.source || 'unknown';
            dataPoint[`${playerName}_hasData`] = true;
          } else {
            // Find the most recent ELO value before this timestamp
            const previousEntries = playerHistory.filter(e => e.timestamp <= timestamp);
            if (previousEntries.length > 0) {
              const lastEntry = previousEntries[previousEntries.length - 1];
              dataPoint[playerName] = lastEntry.elo;
              playerLatestElo[playerName] = lastEntry.elo;
              dataPoint[`${playerName}_hasData`] = false; // Mark as interpolated
            } else {
              // This timestamp is before the player's first entry
              // Don't add a value - the line will start from their first actual data point
              dataPoint[playerName] = null;
              dataPoint[`${playerName}_hasData`] = false;
            }
          }
        }
      });

      // Only add the data point if at least one player has data
      const hasAnyData = selectedPlayers.some(playerName => 
        dataPoint[playerName] !== null && dataPoint[playerName] !== undefined
      );
      
      if (hasAnyData) {
        data.push(dataPoint);
      }
    });

    console.log('📊 Final chart data points:', data.length);
    console.log('📊 Sample data point:', data[0]);
    return data;
  }, [selectedPlayers, playerStats, timeRange, eloHistories, firebaseEloHistories, history]);

  const xAxisTicks = useMemo(() => {
    return chartData
      .filter(point => point.isMonthlyMarker)
      .map(point => point.timestamp);
  }, [chartData]);

  // Custom tooltip to show ELO changes and race details
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const tooltipTimestamp = payload[0]?.payload?.timestamp ?? (Number.isFinite(label) ? label : null);
      const tooltipTitle = tooltipTimestamp
        ? new Date(tooltipTimestamp).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })
        : '';

      return (
        <div style={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.95)', 
          padding: '10px', 
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>{tooltipTitle}</p>
          {payload
            .filter(entry => entry.value !== null && entry.value !== undefined)
            .map((entry, index) => {
              const change = entry.payload[`${entry.dataKey}_change`];
              const event = entry.payload[`${entry.dataKey}_event`];
              const finish = entry.payload[`${entry.dataKey}_finish`];
              const hasData = entry.payload[`${entry.dataKey}_hasData`];
              
              return (
                <div key={index} style={{ marginBottom: '4px' }}>
                  <span style={{ color: entry.color, fontWeight: 'bold' }}>
                    {entry.name}: {Math.round(entry.value)}
                  </span>
                  {hasData && change && (
                    <span style={{ 
                      marginLeft: '8px',
                      color: change > 0 ? '#00C49F' : '#FF8042' 
                    }}>
                      {change > 0 ? '+' : ''}{change}
                    </span>
                  )}
                  {hasData && event && event !== 'Initial (Database Value)' && (
                    <div style={{ fontSize: '0.85em', color: '#666' }}>
                      {event} {finish && `(P${finish})`}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      );
    }
    return null;
  };

  // Custom dot to show actual data points
  const CustomDot = (props) => {
    const { cx, cy, payload, dataKey } = props;
    const hasData = payload[`${dataKey}_hasData`];
    const finish = payload[`${dataKey}_finish`];
    const source = payload[`${dataKey}_source`];
    
    // Only show dots for actual data points
    if (!hasData) return null;
    
    // Remove highlight wins functionality
    if (false) {
      return (
        <circle 
          cx={cx} 
          cy={cy} 
          r={5} 
          fill={props.stroke}
          stroke="#FFD700"
          strokeWidth={2}
        />
      );
    }
    
    // Different dot styles based on data source
    if (source === 'initial') {
      // Initial ELO point - hollow circle
      return (
        <circle 
          cx={cx} 
          cy={cy} 
          r={4} 
          fill="white"
          stroke={props.stroke}
          strokeWidth={2}
        />
      );
    }
    
    if (source === 'firebase') {
      // Firebase data - smaller dots
      return (
        <circle 
          cx={cx} 
          cy={cy} 
          r={2} 
          fill={props.stroke}
          stroke={props.stroke}
          strokeWidth={2}
        />
      );
    }
    
    // Show dots for calculated/tournament data points
    return (
      <circle 
        cx={cx} 
        cy={cy} 
        r={3} 
        fill={props.stroke}
        stroke={props.stroke}
        strokeWidth={1}
      />
    );
  };

  const handlePlayerToggle = (playerName) => {
    const normalizedTarget = normalizePlayerName(playerName);
    setSelectedPlayers(prev => {
      const existingIndex = prev.findIndex(p => normalizePlayerName(p) === normalizedTarget);
      if (existingIndex !== -1) {
        return prev.filter((_, index) => index !== existingIndex);
      }
      if (prev.length >= 20) {
        alert('Maximum 20 players can be compared at once');
        return prev;
      }
      return [...prev, playerName];
    });
  };

  const handleQuickSelect = (type) => {
    switch(type) {
      case 'top3':
        setSelectedPlayers(topPlayers.slice(0, 3).map(p => p.name));
        break;
      case 'top5':
        setSelectedPlayers(topPlayers.slice(0, 5).map(p => p.name));
        break;
      case 'clear':
        setSelectedPlayers([]);
        break;
      default:
        break;
    }
  };

  return (
    <Card style={{ padding: '1.5rem', marginBottom: '2rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <H3 style={{ margin: '0 0 1rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          📈 ELO Rating Over Time
          {selectedPlayers.length > 0 && (
            <Tag intent={Intent.PRIMARY}>{selectedPlayers.length} players selected</Tag>
          )}
        </H3>

        {/* Controls */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          {/* Quick Select Buttons */}
          <ButtonGroup>
            <Button 
              small 
              onClick={() => handleQuickSelect('top3')}
              intent={selectedPlayers.length === 0 ? Intent.PRIMARY : Intent.NONE}
            >
              Top 3 Players
            </Button>
            <Button 
              small 
              onClick={() => handleQuickSelect('top5')}
            >
              Top 5 Players
            </Button>
            <Button 
              small 
              onClick={() => handleQuickSelect('clear')}
              intent={Intent.WARNING}
            >
              Clear All
            </Button>
          </ButtonGroup>

          {/* Time Range */}
          <HTMLSelect
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            options={[
              { label: 'All Time', value: 'all' },
              { label: 'Last 30 Days', value: 'last30' },
              { label: 'Last 60 Days', value: 'last60' },
              { label: 'Last 90 Days', value: 'last90' }
            ]}
          />

          {/* Display Options */}
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            {/* Removed highlight wins checkbox */}
          </div>
        </div>

        {/* Player Search */}
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            Add Player:
          </div>
          <div style={{ position: 'relative' }}>
            <InputGroup
              placeholder="Search for a player..."
              value={searchQuery}
              onChange={(e) => {
                const value = e.target.value;
                setSearchQuery(value);
                if (value.length >= 2) {
                  setIsSearchOpen(true);
                } else {
                  setIsSearchOpen(false);
                }
              }}
              onFocus={() => {
                if (searchQuery.length >= 2) {
                  setIsSearchOpen(true);
                }
              }}
              onBlur={(e) => {
                // Don't close if clicking on the dropdown
                if (!e.relatedTarget || !e.relatedTarget.closest('[role="menu"]')) {
                  setTimeout(() => setIsSearchOpen(false), 150);
                }
              }}
              rightElement={
                <Button 
                  icon="search" 
                  minimal 
                  disabled={searchQuery.length < 2}
                />
              }
              style={{ marginBottom: '1rem' }}
            />
            {isSearchOpen && searchQuery.length >= 2 && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  maxHeight: '200px',
                  overflow: 'auto'
                }}
              >
                {allPlayersWithElo
                  .filter(player => 
                    player.name.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .slice(0, 10)
                  .map(player => (
                    <div
                      key={player.name}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        color: 'var(--text-primary)',
                        fontSize: '0.95rem',
                        fontWeight: '500',
                        backgroundColor: 'transparent'
                      }}
                      onMouseDown={(e) => e.preventDefault()} // Prevent blur
                      onClick={() => {
                        handlePlayerToggle(player.name);
                        setSearchQuery('');
                        setIsSearchOpen(false);
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                        e.target.style.textShadow = '0 0 3px rgba(255, 255, 255, 0.8)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.textShadow = 'none';
                      }}
                    >
                      {player.name}
                    </div>
                  ))
                }
                {allPlayersWithElo.filter(player => 
                  player.name.toLowerCase().includes(searchQuery.toLowerCase())
                ).length === 0 && (
                  <div
                    style={{
                      padding: '8px 12px',
                      color: 'var(--text-secondary)',
                      fontStyle: 'italic',
                      fontSize: '0.9rem'
                    }}
                  >
                    No players found
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Currently Selected Players */}
        {selectedPlayers.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              Selected Players:
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {selectedPlayers.map((playerName, index) => {
                return (
                  <Tag
                    key={playerName}
                    large
                    intent={Intent.PRIMARY}
                    onRemove={() => handlePlayerToggle(playerName)}
                    style={{
                      backgroundColor: COLORS[index % COLORS.length],
                      color: 'white',
                      borderColor: COLORS[index % COLORS.length]
                    }}
                  >
                    {playerName}
                  </Tag>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Quick Select Top Players */}
        <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
          Quick Select:
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {topPlayers.map((player, index) => (
            <Button
              key={player.name}
              small
              intent={selectedPlayers.includes(player.name) ? Intent.SUCCESS : Intent.NONE}
              onClick={() => handlePlayerToggle(player.name)}
              style={{
                backgroundColor: selectedPlayers.includes(player.name) 
                  ? COLORS[selectedPlayers.indexOf(player.name) % COLORS.length] 
                  : undefined
              }}
            >
              {player.name}
            </Button>
          ))}
        </div>
      </div>

      {/* Chart */}
      {selectedPlayers.length > 0 ? (
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
            <XAxis 
              dataKey="timestamp" 
              tick={{ fontSize: 10 }}
              type="number"
              domain={['dataMin', 'dataMax']}
              scale="time"
              ticks={xAxisTicks}
              tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', {
                month: 'short',
                year: '2-digit'
              })}
              angle={-45}
              textAnchor="end"
              height={60}
              interval={0}
            />
            <YAxis 
              domain={['auto', 'auto']}
              tick={{ fontSize: 12 }}
              label={{ value: 'ELO Rating', angle: -90, position: 'insideLeft' }}
              padding={{ top: 20, bottom: 20 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            
            {/* Reference line at 500 (default ELO) */}
            <ReferenceLine 
              y={500} 
              stroke="rgba(0,0,0,0.2)" 
              strokeDasharray="5 5"
              label={{ value: "Starting ELO (500)", position: "left", style: { fontSize: "0.75rem", fill: "#666" } }}
            />
            
            {/* Lines for each selected player */}
            {selectedPlayers.map((playerName, index) => (
              <Line
                key={playerName}
                type="monotone"
                dataKey={playerName}
                stroke={COLORS[index % COLORS.length]}
                strokeWidth={2}
                dot={false}
                connectNulls={false}
                name={playerName}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div style={{ 
          height: 400, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.02)',
          borderRadius: '4px'
        }}>
          <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
            <p style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>
              Select players above to view their ELO progression
            </p>
            <p style={{ fontSize: '0.9rem' }}>
              Click on player names or use quick select buttons
            </p>
          </div>
        </div>
      )}
    </Card>
  );
}

export default EloHistoryChart;
